import React, { useState, useCallback, useMemo } from 'react';
import {
  Container, Button, Row, Col, Alert, Modal, Spinner, Form, Card
} from 'react-bootstrap';
import OrderList from '../components/OrderList';
import EditOrderModal from '../components/EditOrderModal';
import AddOrderModal from '../components/AddOrderModal';
import AddPartsOrderModal from '../components/AddPartsOrderModal';
import OrderTypeSelectionModal from '../components/OrderTypeSelectionModal';
import { getAuthHeaders } from '../utils/authHelpers';
import 'bootstrap/dist/css/bootstrap.min.css';
import Select from 'react-select';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ORDERS_API_URL = `${API_ROOT_URL}/orders`;

function OrdersPage() {
  const [showOrderTypeSelectionModal, setShowOrderTypeSelectionModal] = useState(false);
  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [showAddPartsOrderModal, setShowAddPartsOrderModal] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [managerPassword, setManagerPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  const [pageMessage, setPageMessage] = useState('');
  const [pageMessageType, setPageMessageType] = useState('');
  const [loadingAction, setLoadingAction] = useState(false);
  const [orderListRefreshKey, setOrderListRefreshKey] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState(['All']);
  const [filterOrderType, setFilterOrderType] = useState(['All']);
  const [filterShipping, setFilterShipping] = useState(['All']);

  const allOrderStages = useMemo(() => [
    'All', 'Administration', 'Pre-Production', 'Manufacturing', 'Shipping', 'Completed',
  ], []);

  const orderTypes = useMemo(() => ['All', 'New Cover', 'Reskin', 'Remod', 'Repair', 'Parts'], []);
  const shippingOptions = useMemo(() => ['All', 'Postal Service', 'LTL', 'Piggyback', 'Pickup'], []);
  const [selectedWeek, setSelectedWeek] = useState('all'); // default to all weeks
  const [selectedMonth, setSelectedMonth] = useState('all'); // default to all months
  const [selectedYear, setSelectedYear] = useState('all'); // default to all years

  const handlePageMessage = useCallback((message, type) => {
    setPageMessage(message);
    setPageMessageType(type);
    if (type) {
      setTimeout(() => {
        setPageMessage('');
        setPageMessageType('');
      }, 5000);
    }
  }, []);

  const triggerOrderListRefresh = useCallback(() => {
    setOrderListRefreshKey(prev => prev + 1);
  }, []);

  // --- Order Type Selection ---
  const handleShowOrderTypeSelectionModal = () => {
    handlePageMessage('', '');
    setShowOrderTypeSelectionModal(true);
  };

  const handleCloseOrderTypeSelectionModal = () => {
    setShowOrderTypeSelectionModal(false);
    setSelectedOrderType(null);
  };

  const handleOrderTypeSelected = (type) => {
    setSelectedOrderType(type);
    setShowOrderTypeSelectionModal(false);
    type === 'Parts' ? setShowAddPartsOrderModal(true) : setShowAddOrderModal(true);
  };

  // --- Modals ---
  const handleCloseAddOrderModal = () => {
    setShowAddOrderModal(false);
    setSelectedOrderType(null);
    triggerOrderListRefresh();
  };

  const handleCloseAddPartsOrderModal = () => {
    setShowAddPartsOrderModal(false);
    setSelectedOrderType(null);
    triggerOrderListRefresh();
  };

  const handleEditClick = (order) => {
    handlePageMessage('', '');
    setOrderToEdit(order);
    setShowEditModal(true);
  };

  const handleCloseEditModal = (didUpdate = false) => {
    setShowEditModal(false);
    setOrderToEdit(null);
    if (didUpdate) {
      triggerOrderListRefresh();
    }
  };

  // --- Delete ---
  const handleDeleteClick = (order) => {
    handlePageMessage('', '');
    setOrderToDelete(order);
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setOrderToDelete(null);
    setManagerPassword('');
    setShowPassword(false);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    if (!managerPassword) {
      handlePageMessage('Manager password is required to delete a dealer.', 'warning');
      return;
    }
    handlePageMessage('', '');
    setLoadingAction(true);

    try {
      const response = await fetch(`${ORDERS_API_URL}/${orderToDelete.orderId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ managerSecret: managerPassword }),
      });

      const result = await response.json();
      if (!response.ok) {
        handlePageMessage(result.message || 'Failed to delete order.', 'danger');
      } else {
        handlePageMessage(result.message || 'Order deleted successfully!', 'success');
      }

      triggerOrderListRefresh();
    } catch (error) {
      console.error('Error deleting order:', error);
      handlePageMessage('Network error or server is unreachable while deleting order.', 'danger');
    } finally {
      setLoadingAction(false);
      handleCloseDeleteConfirm();
    }
  };

  const handleMultiFilterChange = (selectedOptions, actionMeta, setState) => {
    // 1. If the list is cleared or empty, default to 'All'.
    if (!selectedOptions || selectedOptions.length === 0) {
      setState(['All']);
      return;
    }

    // Get the string values from the selected options
    const newValues = selectedOptions.map(opt => opt.value);

    // 2. Check what action the user took.
    switch (actionMeta.action) {
      // Case: User clicked an option in the dropdown
      case 'select-option':
        // If the user clicked "All", make it the only selection.
        if (actionMeta.option.value === 'All') {
          setState(['All']);
        } else {
          // If the user clicked a specific option, filter out "All".
          setState(newValues.filter(v => v !== 'All'));
        }
        break;

      // Case: User clicked the 'x' on a selected value
      case 'deselect-option':
      case 'remove-value':
        // If the *last* specific item was removed, default back to "All".
        if (newValues.length === 0) {
          setState(['All']);
        } else {
          // Otherwise, just set the new (smaller) list of values.
          setState(newValues);
        }
        break;

      // Case: User clicked the "Clear" (X) button on the whole component
      case 'clear':
        setState(['All']);
        break;
      
      default:
        setState(newValues);
        break;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus(['All']);
    setFilterOrderType(['All']);
    setFilterShipping(['All']);
    setSelectedYear('all');
    setSelectedMonth('all');
    setSelectedWeek('all');
  };

  return (
    <Container
      fluid
      style={{
        backgroundColor: '#4595ad29',
        minHeight: '100vh',
        paddingTop: '2rem',
        paddingBottom: '2rem'
      }}
    >
      <Container className="my-5">
        {/* Page Header */}
        <Row className="mb-4 align-items-center">
          <Col><h2 className="mb-0">Orders Master Backlog</h2></Col>
          <Col xs="auto">
            <Button variant="success" onClick={handleShowOrderTypeSelectionModal}>
              + Create New Order
            </Button>
          </Col>
        </Row>

        {/* Notifications */}
        {pageMessage && <Alert variant={pageMessageType}>{pageMessage}</Alert>}
        {loadingAction && (
          <div className="d-flex justify-content-center my-3">
            <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
          </div>
        )}

        {/* Filters */}
        <Row className="mb-4 justify-content-center">
          <Col md={12}>
            <Card className="shadow-sm p-3">
              <Row className="align-items-center">
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Group controlId="searchOrders">
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search by Order ID, Invoice Number, Customer, Dealer, and Custom Work"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Label>Production Stage</Form.Label>
                  <Select
                    isMulti
                    options={allOrderStages.map(stage => ({ value: stage, label: stage }))}
                    value={filterStatus.map(s => ({ value: s, label: s }))}
                    onChange={(selected, actionMeta) =>
                      handleMultiFilterChange(selected, actionMeta, setFilterStatus)
                    }
                    classNamePrefix="react-select"
                    isDisabled={loadingAction}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label>Order Type</Form.Label>
                  <Select
                    isMulti
                    options={orderTypes.map(type => ({ value: type, label: type }))}
                    value={filterOrderType.map(t => ({ value: t, label: t }))}
                    onChange={(selected, actionMeta) =>
                      handleMultiFilterChange(selected, actionMeta, setFilterOrderType)
                    }
                    classNamePrefix="react-select"
                    isDisabled={loadingAction}
                  />
                </Col>
              </Row>

              <Row className="mt-3 align-items-center">
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Label>Shipping Method</Form.Label>
                  <Select
                    isMulti
                    options={shippingOptions.map(opt => ({ value: opt, label: opt }))}
                    value={filterShipping.map(s => ({ value: s, label: s }))}
                    onChange={(selected, actionMeta) =>
                      handleMultiFilterChange(selected, actionMeta, setFilterShipping)
                    }
                    classNamePrefix="react-select"
                    isDisabled={loadingAction}
                  />
                </Col>

                <Col className="text-end">
                  <Button variant="outline-secondary" onClick={clearFilters} disabled={loadingAction}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Order List */}
        <OrderList
          key={orderListRefreshKey}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          searchTerm={searchTerm}
          filterStatus={filterStatus}
          filterOrderType={filterOrderType}
          filterShipping={filterShipping}
          selectedWeek={selectedWeek}
          setSelectedWeek={setSelectedWeek}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
        />

        {/* Modals */}
        <OrderTypeSelectionModal
          show={showOrderTypeSelectionModal}
          handleClose={handleCloseOrderTypeSelectionModal}
          onSelectOrderType={handleOrderTypeSelected}
        />

        {selectedOrderType && selectedOrderType !== 'Parts' && (
          <AddOrderModal
            show={showAddOrderModal}
            handleClose={handleCloseAddOrderModal}
            refreshOrders={triggerOrderListRefresh}
            setSubmissionMessage={handlePageMessage}
            initialOrderType={selectedOrderType}
          />
        )}

        {selectedOrderType === 'Parts' && (
          <AddPartsOrderModal
            show={showAddPartsOrderModal}
            handleClose={handleCloseAddPartsOrderModal}
            refreshOrders={triggerOrderListRefresh}
            setSubmissionMessage={handlePageMessage}
          />
        )}

        {showEditModal && orderToEdit && (
          <EditOrderModal
            show={showEditModal}
            handleClose={(didUpdate) => handleCloseEditModal(didUpdate)}
            refreshOrders={triggerOrderListRefresh}
            orderToEdit={orderToEdit}
            setSubmissionMessage={handlePageMessage}
          />
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteConfirm} onHide={handleCloseDeleteConfirm} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete order <strong>{orderToDelete?.customerName}</strong> for <strong>{orderToDelete?.dealerName}</strong>? This action cannot be undone.
            <Form.Group className="mt-3" controlId="managerPassword">
              <Form.Label>Manager Password</Form.Label>
              <div className="d-flex align-items-center mt-2">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter manager password to confirm"
                  value={managerPassword}
                  onChange={(e) => setManagerPassword(e.target.value)}
                  autoFocus
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowPassword(prev => !prev)}
                  style={{ marginLeft: '0.5rem' }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDeleteConfirm}>Cancel</Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteConfirm} 
              disabled={loadingAction || !managerPassword}
            >
              {loadingAction ? <Spinner as="span" animation="border" size="sm" /> : 'Delete'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Container>
  );
}

export default OrdersPage;
